// Client-side weather helpers.
// Open-Meteo + Nominatim both expose CORS=* so we call them directly from the
// browser. This bypasses production backend egress problems and reduces latency.

const WEATHER_CODES = {
  0: "Clear sky", 1: "Mainly clear", 2: "Partly cloudy", 3: "Overcast",
  45: "Fog", 48: "Rime fog",
  51: "Light drizzle", 53: "Drizzle", 55: "Dense drizzle",
  56: "Light freezing drizzle", 57: "Freezing drizzle",
  61: "Light rain", 63: "Rain", 65: "Heavy rain",
  66: "Light freezing rain", 67: "Freezing rain",
  71: "Light snow", 73: "Snow", 75: "Heavy snow", 77: "Snow grains",
  80: "Rain showers", 81: "Heavy showers", 82: "Violent showers",
  85: "Snow showers", 86: "Heavy snow showers",
  95: "Thunderstorm", 96: "Thunderstorm w/ hail", 99: "Thunderstorm w/ heavy hail",
};

async function fetchJson(url, { timeoutMs = 8000 } = {}) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const r = await fetch(url, { signal: ctrl.signal, credentials: "omit" });
    if (!r.ok) throw new Error(`${r.status}`);
    return await r.json();
  } finally {
    clearTimeout(t);
  }
}

export async function reverseGeocode(lat, lon) {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&zoom=10&accept-language=en`;
    const rj = await fetchJson(url, { timeoutMs: 5000 });
    const a = rj?.address || {};
    const city = a.city || a.town || a.village || a.hamlet || a.county;
    const region = a.state || a.region;
    const country = a.country;
    const parts = [city, region, country].filter(Boolean);
    return parts.length ? parts.join(", ") : null;
  } catch {
    return null;
  }
}

export async function getLiveClimate(lat, lon) {
  const url =
    "https://api.open-meteo.com/v1/forecast" +
    `?latitude=${lat}&longitude=${lon}` +
    "&current=temperature_2m,relative_humidity_2m,apparent_temperature," +
    "precipitation,weather_code,wind_speed_10m,uv_index" +
    "&timezone=auto";
  let data = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      data = await fetchJson(url, { timeoutMs: 8000 });
      break;
    } catch {
      if (attempt === 0) await new Promise((r) => setTimeout(r, 600));
    }
  }
  if (!data) {
    return {
      climate: null,
      location_label: null,
      warning: "Live weather is temporarily unavailable. Your scan will still work.",
    };
  }
  const cur = data.current || {};
  const code = cur.weather_code;
  const climate = {
    temperature_c: cur.temperature_2m ?? null,
    apparent_temperature_c: cur.apparent_temperature ?? null,
    humidity_pct: cur.relative_humidity_2m ?? null,
    precipitation_mm: cur.precipitation ?? null,
    uv_index: cur.uv_index ?? null,
    wind_kph: cur.wind_speed_10m ?? null,
    weather_code: code ?? null,
    weather_description: code != null ? WEATHER_CODES[code] || "Unknown" : null,
    latitude: lat,
    longitude: lon,
    timezone: data.timezone ?? null,
  };
  const location_label = await reverseGeocode(lat, lon);
  return { climate, location_label };
}

export async function getForecast(lat, lon) {
  const url =
    "https://api.open-meteo.com/v1/forecast" +
    `?latitude=${lat}&longitude=${lon}` +
    "&daily=temperature_2m_max,temperature_2m_min,precipitation_sum," +
    "precipitation_probability_max,uv_index_max,weather_code" +
    "&forecast_days=7&timezone=auto";
  let data = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      data = await fetchJson(url, { timeoutMs: 8000 });
      break;
    } catch {
      if (attempt === 0) await new Promise((r) => setTimeout(r, 600));
    }
  }
  if (!data) return { daily: [], warning: "Forecast temporarily unavailable." };
  const d = data.daily || {};
  const dates = d.time || [];
  const daily = dates.map((dt, i) => {
    const code = (d.weather_code || [])[i] ?? null;
    return {
      date: dt,
      temp_max_c: (d.temperature_2m_max || [])[i] ?? null,
      temp_min_c: (d.temperature_2m_min || [])[i] ?? null,
      precipitation_mm: (d.precipitation_sum || [])[i] ?? null,
      precipitation_probability_pct: (d.precipitation_probability_max || [])[i] ?? null,
      uv_index_max: (d.uv_index_max || [])[i] ?? null,
      weather_code: code,
      weather_description: code != null ? WEATHER_CODES[code] || null : null,
    };
  });
  return { daily, timezone: data.timezone ?? null };
}

export async function getHourly(lat, lon) {
  const url =
    "https://api.open-meteo.com/v1/forecast" +
    `?latitude=${lat}&longitude=${lon}` +
    "&hourly=temperature_2m,relative_humidity_2m,uv_index" +
    "&forecast_days=1&timezone=auto";
  let data = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      data = await fetchJson(url, { timeoutMs: 8000 });
      break;
    } catch {
      if (attempt === 0) await new Promise((r) => setTimeout(r, 600));
    }
  }
  if (!data) return { hours: [], warning: "Hourly data temporarily unavailable." };
  const h = data.hourly || {};
  const times = h.time || [];
  const hours = times.map((t, i) => ({
    time: t, // ISO like "2026-10-05T14:00"
    temp_c: (h.temperature_2m || [])[i] ?? null,
    humidity_pct: (h.relative_humidity_2m || [])[i] ?? null,
    uv_index: (h.uv_index || [])[i] ?? null,
  }));
  return { hours, timezone: data.timezone ?? null };
}

export async function searchCity(q) {
  const term = (q || "").trim();
  if (term.length < 2) return [];
  try {
    const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(term)}&count=5&language=en&format=json`;
    const rj = await fetchJson(url, { timeoutMs: 6000 });
    return (rj.results || []).map((r) => {
      const parts = [r.name, r.admin1, r.country].filter(Boolean);
      return {
        name: r.name,
        admin1: r.admin1,
        country: r.country,
        country_code: r.country_code,
        latitude: r.latitude,
        longitude: r.longitude,
        label: parts.length ? parts.join(", ") : r.name,
      };
    });
  } catch {
    return [];
  }
}
